import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import PlaceholderImage from '../components/PlaceholderImage';
import Modal from '../components/Modal';
import IdentityCard from '../components/IdentityCard';
import { caseStudyHomeData, HomeCaseStudyCategory } from './CaseStudy';

const CASE_STUDY_FILTERS: ('All' | HomeCaseStudyCategory)[] = ['All', 'UI/UX Design', 'UX Research', 'Product Design'];

const Home: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedCaseStudy, setSelectedCaseStudy] = useState<string | null>(null);
  const [activeFilter, setActiveFilter] = useState<'All' | HomeCaseStudyCategory>('All');

  const selectedStudy = useMemo(
    () => caseStudyHomeData.find((study) => study.id === selectedCaseStudy) ?? null,
    [selectedCaseStudy]
  );

  const filteredCaseStudies = useMemo(
    () => activeFilter === 'All'
      ? caseStudyHomeData
      : caseStudyHomeData.filter((study) => study.categories.includes(activeFilter)),
    [activeFilter]
  );

  const handleCaseStudyClick = (studyId: string) => {
    setSelectedCaseStudy(studyId);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedCaseStudy(null);
  };

  return (
    <div className="App">
      <IdentityCard />

      <section className="hero-section">
        <div className="container">
          <div className="hero-card animate-fade-in-up">
            <div className="hero-card-image">
              <PlaceholderImage
                src="/img/website-banner.png"
                alt="Website banner featuring a clean, modern design used across Sanchita's site to represent her research branding and datavisualization-focused work."
                className="w-full h-full object-cover"
              />
            </div>
            <div className="hero-card-content">
              <h1 className="hero-card-title">Sanchita Kamath</h1>
              <p className="hero-card-subtitle">
                UX Researcher &amp; Ph.D. Candidate in Information Sciences
              </p>
              <p className="hero-card-description">
                Advancing the field of human-computer interaction through methodological innovation at the University of Illinois at Urbana-Champaign. I transform complex human behavior patterns into evidence-based design frameworks that influence both academic discourse and industry practice, with a focus on accessibility, inclusive design, and mixed-methods research.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="case-studies-section">
        <div className="container">
          <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap', marginBottom: '2rem' }}>
            {CASE_STUDY_FILTERS.map((filter) => (
              <button
                key={filter}
                onClick={() => setActiveFilter(filter)}
                style={{
                  padding: '0.5rem 1.1rem',
                  borderRadius: 20,
                  border: activeFilter === filter ? 'none' : '1.5px solid #d6d6d6',
                  background: activeFilter === filter ? '#2563eb' : '#fff',
                  color: activeFilter === filter ? '#fff' : '#555',
                  fontWeight: 600,
                  fontSize: '0.88rem',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                }}
              >
                {filter}
              </button>
            ))}
          </div>
          <div className="grid grid-2 animate-stagger">
            {filteredCaseStudies.map((study) => (
              <div key={study.id} className="card">
                <PlaceholderImage
                  src={study.image}
                  alt={`${study.title} - ${study.description}`}
                  className="card-image"
                />
                <h3 className="card-title">{study.title}</h3>
                <div className="card-tags">
                  {study.tags.map((tag) => (
                    <span key={tag} className="tag">{tag}</span>
                  ))}
                </div>
                <p className="card-description">{study.description}</p>
                {study.showModal ? (
                  <button
                    className="btn-link"
                    onClick={() => handleCaseStudyClick(study.id)}
                  >
                    View Case Study
                  </button>
                ) : (
                  <Link to={`/case-study/${study.id}`} className="btn-link">
                    View Case Study
                  </Link>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-compact bg-secondary">
        <div className="container">
          <div className="grid grid-3 animate-stagger">
            <div className="card">
              <h3 className="card-title">Research Portfolio</h3>
              <p className="card-description">
                Explore my comprehensive research work in human-computer interaction, accessibility, and cognitive science applications.
              </p>
              <Link to="/portfolio" className="btn-link">
                View Portfolio
              </Link>
            </div>
            <div className="card">
              <h3 className="card-title">Writings</h3>
              <p className="card-description">
                Read my perspectives on UX research methodologies, cognitive psychology, and ethical design practices.
              </p>
              <Link to="/writings" className="btn-link">
                Read Articles
              </Link>
            </div>
            <div className="card">
              <h3 className="card-title">About Me</h3>
              <p className="card-description">
                Learn more about my background, research interests, and approach to human-centered design.
              </p>
              <Link to="/about" className="btn-link">
                Learn More
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Modal
        isOpen={isModalOpen}
        onClose={closeModal}
        title={selectedStudy?.modalTitle ?? ''}
        technologies={selectedStudy?.technologies ?? []}
        date={selectedStudy?.date ?? ''}
      >
        {selectedStudy && (
          <div dangerouslySetInnerHTML={{ __html: selectedStudy.modalContent }} />
        )}
      </Modal>
    </div>
  );
};

export default Home;
